/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Briefcase, User, Mail, Lock, ArrowLeft, ArrowRight,
  KeyRound, Eye, EyeOff, Loader2,
  UserPlus, Calendar, Search, Award,
} from 'lucide-react';
import axios from 'axios';
import './RecruiterLogin.css';

/* ─── Password Input with toggle ─── */
function PwInput({ name, placeholder, onChange, icon: Icon = Lock }) {
  const [show, setShow] = useState(false);
  return (
    <div className="rec-iw">
      <Icon size={18} className="rec-iw-icon" />
      <input
        className="rec-input"
        type={show ? 'text' : 'password'}
        name={name}
        placeholder={placeholder}
        onChange={onChange}
        required
        autoComplete="off"
      />
      <button
        type="button"
        className="rec-pw-toggle"
        onClick={() => setShow(!show)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
      <span className="rec-fline" />
    </div>
  );
}

/* ═══════════════════════════════════════════
   SUCCESS OVERLAY — rendered via portal to document.body
   ═══════════════════════════════════════════ */
function SuccessOverlay({ onDone }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 1200);
    return () => clearTimeout(timer);
  }, [onDone]);

  return ReactDOM.createPortal(
    <div className="rec-success-overlay">
      <div className="rec-success-card">

        {/* animated checkmark ring */}
        <div className="rec-success-ring">
          <svg className="rec-success-svg" viewBox="0 0 52 52">
            <circle className="rec-success-circle" cx="26" cy="26" r="23" />
            <path className="rec-success-check" d="M14 27 l8 8 l16 -16" />
          </svg>
        </div>

        <p className="rec-success-text">Welcome Back</p>
        <span className="rec-success-sub">Redirecting to dashboard…</span>

        {/* progress bar */}
        <div className="rec-success-bar">
          <div className="rec-success-bar-fill" />
        </div>

      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function RecruiterLogin({ onLoginSuccess, onBack }) {
  const [view, setView] = useState('login');
  const [animKey, setAnimKey] = useState(0);
  const leftRef = useRef(null);
  const partRef = useRef(null);

  const switchView = useCallback((v) => {
    setAnimKey((k) => k + 1);
    setView(v);
  }, []);

  /* ── Particles ── */
  useEffect(() => {
    const c = partRef.current;
    if (!c) return;
    const ps = [];
    for (let i = 0; i < 30; i++) {
      const p = document.createElement('div');
      p.className = 'rec-pt';
      const sz = Math.random() * 2 + .8;
      p.style.cssText = `
        left:${Math.random() * 100}%;
        bottom:${Math.random() * -15}%;
        width:${sz}px;height:${sz}px;
        animation-duration:${Math.random() * 18 + 14}s;
        animation-delay:${Math.random() * 12}s;
      `;
      c.appendChild(p);
      ps.push(p);
    }
    return () => ps.forEach((p) => p.remove());
  }, []);

  /* ── Orb parallax ── */
  const onMouse = useCallback((e) => {
    const l = leftRef.current;
    if (!l) return;
    const r = l.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) / (r.width / 2);
    const dy = (e.clientY - cy) / (r.height / 2);
    l.querySelectorAll('.rec-orb').forEach((o, i) => {
      const sp = (i + 1) * 12;
      o.style.transform = `translate(${dx * sp}px,${dy * sp}px)`;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouse);
    return () => window.removeEventListener('mousemove', onMouse);
  }, [onMouse]);

  const subtitles = {
    login: 'Welcome back! Access your hiring dashboard.',
    forgot: 'Enter your details to reset your password.',
  };

  return (
    <div className="rec-w">
      {/* ━━━ LEFT PANEL ━━━ */}
      <div className="rec-left" ref={leftRef}>
        <div className="rec-left-grid" />
        <div className="rec-orb rec-orb--1" />
        <div className="rec-orb rec-orb--2" />
        <div className="rec-orb rec-orb--3" />
        <div className="rec-parts" ref={partRef} />
        
        <div className="rec-left-content">
          <div className="rec-left-brand">
            <div className="rec-brand-box">
              <Briefcase size={24} color="#fff" />
            </div>
            <span className="rec-brand-name">TalentOza</span>
          </div>

          <h1 className="rec-left-h">
            Recruiter<br />
            <span className="rec-gr">Dashboard</span>
          </h1>

          <p className="rec-left-p">
            Browse open requisitions, submit candidate profiles, 
            schedule interviews, and track the entire hiring pipeline.
          </p>

          <div className="rec-left-feats">
            <div className="rec-left-feat">
              <div className="rec-feat-icon">
                <Search size={18} color="#c4b5fd" />
              </div>
              <div className="rec-feat-text">
                <h4>Job Board Access</h4>
                <p>Browse and filter open positions across departments</p>
              </div>
            </div>
            <div className="rec-left-feat">
              <div className="rec-feat-icon">
                <UserPlus size={18} color="#e9d5ff" />
              </div>
              <div className="rec-feat-text">
                <h4>Submit Candidates</h4>
                <p>Upload resumes and match profiles to requisitions</p>
              </div>
            </div>
            <div className="rec-left-feat">
              <div className="rec-feat-icon">
                <Calendar size={18} color="#a78bfa" />
              </div>
              <div className="rec-feat-text">
                <h4>Interview Scheduling</h4>
                <p>Coordinate interviews with built-in calendar tools</p>
              </div>
            </div>
            <div className="rec-left-feat">
              <div className="rec-feat-icon">
                <Award size={18} color="#ddd6fe" />
              </div>
              <div className="rec-feat-text">
                <h4>Track Progress</h4>
                <p>Real-time status updates on every submission</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ━━━ RIGHT PANEL ━━━ */}
      <div className="rec-right">
        {onBack && (
          <button className="rec-back" onClick={onBack}>
            <ArrowLeft size={16} /> Back
          </button>
        )}

        <div className="rec-form-card">
          {/* Header */}
          <div className="rec-header">
            <div className="rec-icon-wrap">
              <div className="rec-icon-box">
                <Briefcase size={30} color="var(--r-pri)" />
              </div>
              <div className="rec-lock-badge">
                <Lock size={11} color="#fff" />
              </div>
            </div>
            <h1 className="rec-h1">
              {view === 'login' ? 'Recruiter Login' : 'Reset Password'}
            </h1>
            <p className="rec-sub">{subtitles[view]}</p>
            <div className="rec-sec-badge">
              <span className="rec-sec-dot" />
              Secure Connection
            </div>
          </div>

          {/* Form views */}
          <div className="rec-view" key={animKey}>
            {view === 'login' && (
              <LoginForm onSwitchView={switchView} onLoginSuccess={onLoginSuccess} />
            )}
            {view === 'forgot' && (
              <ForgotPasswordForm onSwitchView={switchView} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LOGIN FORM
   ═══════════════════════════════════════════ */
function LoginForm({ onSwitchView, onLoginSuccess }) {
  const [formData, setFormData] = useState({ loginIdentifier: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/recruiters/login`,
        formData
      );
      const recruiterData = response.data.recruiter;
      localStorage.setItem('recruiterProfileData', JSON.stringify(recruiterData));
      setIsLoading(false);
      setShowSuccess(true);
    } catch (err) {
      console.error('Login Error:', err);
      alert(err.response?.data?.message || 'Login Failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* portal renders to document.body — always fullscreen */}
      {showSuccess && (
        <SuccessOverlay
          onDone={() => {
            setShowSuccess(false);
            if (onLoginSuccess) onLoginSuccess();
          }}
        />
      )}

      <form className="rec-form" onSubmit={handleLogin}>
        <div className="rec-fg">
          <label className="rec-label">Username or Email</label>
          <div className="rec-iw">
            <User size={18} className="rec-iw-icon" />
            <input
              className="rec-input"
              type="text"
              name="loginIdentifier"
              onChange={handleChange}
              placeholder="Enter username or email"
              required
              autoComplete="off"
            />
            <span className="rec-fline" />
          </div>
        </div>

        <div className="rec-fg">
          <label className="rec-label">Password</label>
          <PwInput name="password" placeholder="Enter password" onChange={handleChange} />
        </div>

        <div className="rec-actions">
          <button type="button" className="rec-link" onClick={() => onSwitchView('forgot')}>
            Forgot Password?
          </button>
        </div>

        <button
          type="submit"
          className={`rec-btn ${isLoading ? 'rec-btn--loading' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 size={20} className="rec-spinner" />
          ) : (
            <>
              <span>Log In</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>

        <div className="rec-divider">
          <span className="rec-div-line" />
          <span className="rec-div-text">Need Access?</span>
          <span className="rec-div-line" />
        </div>

        <div className="rec-info">
          Contact Admin to create an account
        </div>
      </form>
    </>
  );
}

/* ═══════════════════════════════════════════
   FORGOT PASSWORD FORM
   ═══════════════════════════════════════════ */
function ForgotPasswordForm({ onSwitchView }) {
  const [formData, setFormData] = useState({
    email: '', newPassword: '', confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/recruiters/forgot-password`,
        {
          email: formData.email,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }
      );
      alert('Password updated successfully. Please login.');
      onSwitchView('login');
    } catch (err) {
      console.error('Reset Error:', err);
      alert(err.response?.data?.message || 'Reset failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="rec-form" onSubmit={handleReset}>
      <div className="rec-fg">
        <label className="rec-label">Registered Email</label>
        <div className="rec-iw">
          <Mail size={18} className="rec-iw-icon" />
          <input
            className="rec-input"
            type="email"
            name="email"
            onChange={handleChange}
            placeholder="Enter email"
            required
            autoComplete="off"
          />
          <span className="rec-fline" />
        </div>
      </div>

      <div className="rec-fg">
        <label className="rec-label">New Password</label>
        <PwInput
          name="newPassword"
          placeholder="New password"
          onChange={handleChange}
          icon={KeyRound}
        />
      </div>

      <div className="rec-fg">
        <label className="rec-label">Confirm Password</label>
        <PwInput
          name="confirmPassword"
          placeholder="Confirm new password"
          onChange={handleChange}
          icon={KeyRound}
        />
      </div>

      <button
        type="submit"
        className={`rec-btn ${isLoading ? 'rec-btn--loading' : ''}`}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 size={20} className="rec-spinner" />
        ) : (
          'Update Password'
        )}
      </button>

      <button type="button" className="rec-back-btn" onClick={() => onSwitchView('login')}>
        <ArrowLeft size={16} /> Back to Login
      </button>
    </form>
  );
}